import { Program, batch, KeyMsg, quit } from '@boba-cli/tea';
export { batch, sequence, tick } from '@boba-cli/tea';
import { newBinding, matches } from '@boba-cli/key';
import { Style } from '@boba-cli/chapstick';
export { Style } from '@boba-cli/chapstick';
import { CodeModel } from '@boba-cli/code';
import { FilepickerModel } from '@boba-cli/filepicker';
import { FiletreeModel } from '@boba-cli/filetree';
import { HelpModel, HelpBubble } from '@boba-cli/help';
import { ListModel } from '@boba-cli/list';
export { DefaultItem, ListModel } from '@boba-cli/list';
import { renderMarkdown } from '@boba-cli/markdown';
import { PaginatorType, PaginatorModel } from '@boba-cli/paginator';
import { ProgressModel } from '@boba-cli/progress';
import { line, SpinnerModel } from '@boba-cli/spinner';
export { dot, ellipsis, line, meter, miniDot, moon, points, pulse } from '@boba-cli/spinner';
import { StatusbarModel } from '@boba-cli/statusbar';
import { StopwatchModel } from '@boba-cli/stopwatch';
import { TableModel } from '@boba-cli/table';
import { TextareaModel } from '@boba-cli/textarea';
import { EchoMode, TextInputModel } from '@boba-cli/textinput';
export { CursorMode, EchoMode } from '@boba-cli/textinput';
import { TimerModel } from '@boba-cli/timer';
import { ViewportModel } from '@boba-cli/viewport';

// src/app-builder.ts
function render(node) {
  if (typeof node === "string") {
    return node;
  }
  if (isTextNode(node)) {
    return renderTextNode(node);
  }
  if (isLayoutNode(node)) {
    return renderLayoutNode(node);
  }
  if (isComponentView(node)) {
    return node.view;
  }
  return "";
}
function isTextNode(node) {
  return typeof node === "object" && "_type" in node && node._type === "text";
}
function isLayoutNode(node) {
  return typeof node === "object" && "_type" in node && (node._type === "vstack" || node._type === "hstack");
}
function isComponentView(node) {
  return typeof node === "object" && "_type" in node && node._type === "component";
}
function renderTextNode(node) {
  let style = new Style();
  if (node._bold) {
    style = style.bold(true);
  }
  if (node._dim && !node._foreground) {
    style = style.foreground("#888888");
  }
  if (node._italic) {
    style = style.italic(true);
  }
  if (node._foreground) {
    style = style.foreground(node._foreground);
  }
  if (node._background) {
    style = style.background(node._background);
  }
  return style.render(node.content);
}
function renderLayoutNode(node) {
  const renderedChildren = node.children.map((child) => render(child)).filter((s) => s.length > 0);
  if (node._type === "vstack") {
    const separator2 = node.spacing > 0 ? "\n".repeat(node.spacing + 1) : "\n";
    return renderedChildren.join(separator2);
  }
  const separator = node.spacing > 0 ? " ".repeat(node.spacing) : "";
  return renderedChildren.join(separator);
}

// src/view/nodes.ts
function text(content) {
  return createTextNode(content, false, false, false, void 0, void 0);
}
function createTextNode(content, bold, dim, italic, foreground, background) {
  return {
    _type: "text",
    content,
    _bold: bold,
    _dim: dim,
    _italic: italic,
    _foreground: foreground,
    _background: background,
    bold() {
      return createTextNode(content, true, dim, italic, foreground, background);
    },
    dim() {
      return createTextNode(content, bold, true, italic, foreground, background);
    },
    italic() {
      return createTextNode(content, bold, dim, true, foreground, background);
    },
    foreground(color) {
      return createTextNode(content, bold, dim, italic, color, background);
    },
    background(color) {
      return createTextNode(content, bold, dim, italic, foreground, color);
    }
  };
}
function vstack(...children) {
  return {
    _type: "vstack",
    children,
    spacing: 0
  };
}
function hstack(...children) {
  return {
    _type: "hstack",
    children,
    spacing: 0
  };
}
function spacer(height = 1) {
  return "\n".repeat(height);
}
function divider(char = "\u2500", width = 40) {
  return char.repeat(width);
}
function when(condition, node) {
  return condition ? node : "";
}
function choose(condition, ifTrue, ifFalse) {
  return condition ? ifTrue : ifFalse;
}
function map(items, render2) {
  return items.map(render2);
}
function componentView(view) {
  return {
    _type: "component",
    view
  };
}

// src/app-builder.ts
var AppBuilder = class _AppBuilder {
  #initialState;
  #components;
  #keyHandlers;
  #messageHandlers;
  #initHandler;
  #viewFn;
  constructor(initialState, components, keyHandlers, messageHandlers, initHandler, viewFn) {
    this.#initialState = initialState;
    this.#components = components;
    this.#keyHandlers = keyHandlers;
    this.#messageHandlers = messageHandlers;
    this.#initHandler = initHandler;
    this.#viewFn = viewFn;
  }
  /**
   * Create a new AppBuilder instance.
   * @internal
   */
  static create() {
    return new _AppBuilder(void 0, [], [], [], void 0, void 0);
  }
  /**
   * Set the initial application state.
   *
   * @remarks
   * This should typically be called early in the builder chain. If called after
   * registering key handlers or a view function, those will be preserved but
   * their type information will be updated to reflect the new state type.
   *
   * @example
   * ```typescript
   * createApp()
   *   .state({ count: 0, name: 'World' })
   * ```
   *
   * @typeParam S - The application state type
   * @param initial - The initial state object
   * @returns A new {@link AppBuilder} with the state type parameter set
   *
   * @public
   */
  state(initial) {
    return new _AppBuilder(
      initial,
      this.#components,
      this.#keyHandlers,
      this.#messageHandlers,
      this.#initHandler,
      this.#viewFn
    );
  }
  /**
   * Register a component with a unique key.
   *
   * @remarks
   * Components are TEA models wrapped in a {@link ComponentBuilder} that
   * manages their lifecycle. The component's rendered view is available in
   * the view function via `components[key]`.
   *
   * @example
   * ```typescript
   * createApp()
   *   .component('loading', spinner())
   *   .component('input', textInput())
   * ```
   *
   * @typeParam K - The component key (string literal type)
   * @typeParam M - The component model type
   * @param key - Unique identifier for this component
   * @param builder - Component builder implementing init/update/view
   * @returns A new {@link AppBuilder} with the component registered
   *
   * @public
   */
  component(key, builder) {
    const newComponents = [...this.#components, { key, builder }];
    return new _AppBuilder(
      this.#initialState,
      newComponents,
      this.#keyHandlers,
      this.#messageHandlers,
      this.#initHandler,
      this.#viewFn
    );
  }
  /**
   * Register a key handler.
   *
   * @remarks
   * Key handlers receive an {@link EventContext} with the current state and
   * components. Multiple keys can be bound to the same handler by passing an
   * array of key strings. Key strings support modifiers like 'ctrl+c', 'alt+enter'.
   *
   * @example
   * ```typescript
   * createApp()
   *   .onKey('q', ({ quit }) => quit())
   *   .onKey(['up', 'k'], ({ state, update }) => update({ index: state.index - 1 }))
   *   .onKey('ctrl+c', ({ quit }) => quit())
   * ```
   *
   * @param keys - Single key string or array of key strings
   * @param handler - Function to call when any of the keys are pressed
   * @returns A new {@link AppBuilder} with the key handler registered
   *
   * @public
   */
  onKey(keys, handler) {
    const keyArray = Array.isArray(keys) ? keys : [keys];
    const newHandlers = [...this.#keyHandlers, { keys: keyArray, handler }];
    return new _AppBuilder(
      this.#initialState,
      this.#components,
      newHandlers,
      this.#messageHandlers,
      this.#initHandler,
      this.#viewFn
    );
  }
  /**
   * Set the initialization handler.
   *
   * @remarks
   * The init handler is called once when the application starts. Use it to
   * schedule initial async operations like fetching data or starting timers.
   * Calling this method multiple times will replace the previous handler.
   *
   * @example
   * ```typescript
   * createApp()
   *   .onInit(({ schedule }) => {
   *     schedule(tick(1000, () => new MyMessage()))
   *   })
   * ```
   *
   * @param handler - Function to call on initialization
   * @returns A new {@link AppBuilder} with the init handler registered
   *
   * @public
   */
  onInit(handler) {
    return new _AppBuilder(
      this.#initialState,
      this.#components,
      this.#keyHandlers,
      this.#messageHandlers,
      handler,
      this.#viewFn
    );
  }
  /**
   * Register a message handler for a specific message type.
   *
   * @remarks
   * Message handlers are called when a message of the specified type is received.
   * Use this to handle async callbacks from commands scheduled via `onInit()`
   * or other message handlers.
   *
   * @example
   * ```typescript
   * class DownloadComplete {
   *   constructor(public packageName: string) {}
   * }
   *
   * createApp()
   *   .onMessage(DownloadComplete, ({ msg, update, schedule }) => {
   *     update({ currentPackage: msg.packageName })
   *     schedule(tick(500, () => new DownloadComplete('next-package')))
   *   })
   * ```
   *
   * @param msgClass - The message class constructor to match
   * @param handler - Function to call when a message of this type is received
   * @returns A new {@link AppBuilder} with the message handler registered
   *
   * @public
   */
  onMessage(msgClass, handler) {
    const newHandlers = [
      ...this.#messageHandlers,
      { msgClass, handler }
    ];
    return new _AppBuilder(
      this.#initialState,
      this.#components,
      this.#keyHandlers,
      newHandlers,
      this.#initHandler,
      this.#viewFn
    );
  }
  /**
   * Set the view function.
   *
   * @remarks
   * The view function is called on every render cycle and receives the current
   * state and component views. It should return a {@link ViewNode} tree
   * describing the UI to display.
   *
   * @example
   * ```typescript
   * createApp()
   *   .view(({ state, components }) => vstack(
   *     text('Hello ' + state.name),
   *     components.spinner
   *   ))
   * ```
   *
   * @param fn - Function that returns a {@link ViewNode} tree
   * @returns A new {@link AppBuilder} with the view function set
   *
   * @public
   */
  view(fn) {
    return new _AppBuilder(
      this.#initialState,
      this.#components,
      this.#keyHandlers,
      this.#messageHandlers,
      this.#initHandler,
      fn
    );
  }
  /**
   * Build the application.
   *
   * @remarks
   * Finalizes the builder chain and creates an {@link App} instance ready
   * to run. This method must be called after setting a view function via
   * {@link AppBuilder.view}.
   *
   * @throws Error if no view function has been set
   *
   * @returns The built {@link App} ready to run
   *
   * @public
   */
  build() {
    if (this.#viewFn === void 0) {
      throw new Error("AppBuilder: view() must be called before build()");
    }
    const initialState = this.#initialState;
    const components = this.#components;
    const keyHandlers = this.#keyHandlers;
    const messageHandlers = this.#messageHandlers;
    const initHandler = this.#initHandler;
    const viewFn = this.#viewFn;
    const model = new GeneratedModel(
      initialState,
      components,
      keyHandlers,
      messageHandlers,
      initHandler,
      viewFn
    );
    return {
      async run(options) {
        const program = new Program(model, { platform: options.platform });
        const result = await program.run();
        return { state: result.model.getUserState() };
      },
      getModel() {
        return model;
      }
    };
  }
};
function createApp() {
  return AppBuilder.create();
}
var GeneratedModel = class _GeneratedModel {
  #userState;
  #componentModels;
  #componentBuilders;
  #keyHandlers;
  #messageHandlers;
  #initHandler;
  #viewFn;
  constructor(userState, components, keyHandlers, messageHandlers, initHandler, viewFn, componentModels) {
    this.#userState = userState;
    this.#keyHandlers = keyHandlers;
    this.#messageHandlers = messageHandlers;
    this.#initHandler = initHandler;
    this.#viewFn = viewFn;
    this.#componentBuilders = /* @__PURE__ */ new Map();
    for (const { key, builder } of components) {
      this.#componentBuilders.set(key, builder);
    }
    this.#componentModels = componentModels ?? /* @__PURE__ */ new Map();
  }
  getUserState() {
    return this.#userState;
  }
  init() {
    const cmds = [];
    for (const [key, builder] of this.#componentBuilders) {
      const [model, cmd] = builder.init();
      this.#componentModels.set(key, model);
      if (cmd) {
        cmds.push(cmd);
      }
    }
    if (this.#initHandler) {
      const scheduledCmds = [];
      const componentUpdates = [];
      const ctx = {
        state: this.#userState,
        schedule: (cmd) => {
          if (cmd) scheduledCmds.push(cmd);
        },
        sendToComponent: (key, fn) => {
          const currentModel = this.#componentModels.get(key);
          if (currentModel !== void 0) {
            const [nextModel, cmd] = fn(currentModel);
            componentUpdates.push({
              key,
              model: nextModel,
              cmd
            });
          }
        }
      };
      this.#initHandler(ctx);
      for (const { key, model, cmd } of componentUpdates) {
        this.#componentModels.set(key, model);
        if (cmd) cmds.push(cmd);
      }
      cmds.push(...scheduledCmds);
    }
    return cmds.length > 0 ? batch(...cmds) : null;
  }
  update(msg) {
    if (msg instanceof KeyMsg) {
      for (const { keys, handler } of this.#keyHandlers) {
        const binding = newBinding({ keys });
        if (matches(msg, binding)) {
          let nextUserState = this.#userState;
          let shouldQuit = false;
          const componentUpdates = [];
          const ctx = {
            state: this.#userState,
            components: this.#buildComponentViews(),
            update: (patch) => {
              nextUserState = { ...nextUserState, ...patch };
            },
            setState: (newState) => {
              nextUserState = newState;
            },
            quit: () => {
              shouldQuit = true;
            },
            sendToComponent: (key, fn) => {
              const currentModel = this.#componentModels.get(key);
              if (currentModel !== void 0) {
                const [nextModel, cmd] = fn(currentModel);
                componentUpdates.push({
                  key,
                  model: nextModel,
                  cmd
                });
              }
            }
          };
          handler(ctx);
          if (shouldQuit) {
            return [this, quit()];
          }
          const stateChanged = nextUserState !== this.#userState;
          const componentsChanged = componentUpdates.length > 0;
          if (stateChanged || componentsChanged) {
            const newComponentModels2 = new Map(this.#componentModels);
            const cmds2 = [];
            for (const { key, model, cmd } of componentUpdates) {
              newComponentModels2.set(key, model);
              if (cmd) {
                cmds2.push(cmd);
              }
            }
            const next = new _GeneratedModel(
              nextUserState,
              Array.from(this.#componentBuilders.entries()).map(([key, builder]) => ({
                key,
                builder
              })),
              this.#keyHandlers,
              this.#messageHandlers,
              this.#initHandler,
              this.#viewFn,
              newComponentModels2
            );
            return [next, cmds2.length > 0 ? batch(...cmds2) : null];
          }
          return [this, null];
        }
      }
    }
    for (const { msgClass, handler } of this.#messageHandlers) {
      if (msg instanceof msgClass) {
        let nextUserState = this.#userState;
        let shouldQuit = false;
        const scheduledCmds = [];
        const componentUpdates = [];
        const ctx = {
          msg,
          state: this.#userState,
          components: this.#buildComponentViews(),
          update: (patch) => {
            nextUserState = { ...nextUserState, ...patch };
          },
          setState: (newState) => {
            nextUserState = newState;
          },
          quit: () => {
            shouldQuit = true;
          },
          schedule: (cmd) => {
            if (cmd) scheduledCmds.push(cmd);
          },
          sendToComponent: (key, fn) => {
            const currentModel = this.#componentModels.get(key);
            if (currentModel !== void 0) {
              const [nextModel, cmd] = fn(currentModel);
              componentUpdates.push({
                key,
                model: nextModel,
                cmd
              });
            }
          }
        };
        handler(ctx);
        if (shouldQuit) {
          return [this, quit()];
        }
        const stateChanged = nextUserState !== this.#userState;
        const componentsChanged = componentUpdates.length > 0;
        const hasScheduled = scheduledCmds.length > 0;
        if (stateChanged || componentsChanged || hasScheduled) {
          const newComponentModels2 = new Map(this.#componentModels);
          const cmds2 = [...scheduledCmds];
          for (const { key, model, cmd } of componentUpdates) {
            newComponentModels2.set(key, model);
            if (cmd) {
              cmds2.push(cmd);
            }
          }
          const next = new _GeneratedModel(
            nextUserState,
            Array.from(this.#componentBuilders.entries()).map(([key, builder]) => ({
              key,
              builder
            })),
            this.#keyHandlers,
            this.#messageHandlers,
            this.#initHandler,
            this.#viewFn,
            newComponentModels2
          );
          return [next, cmds2.length > 0 ? batch(...cmds2) : null];
        }
        return [this, null];
      }
    }
    const cmds = [];
    let anyComponentChanged = false;
    const newComponentModels = new Map(this.#componentModels);
    for (const [key, builder] of this.#componentBuilders) {
      const currentModel = this.#componentModels.get(key);
      if (currentModel === void 0) {
        continue;
      }
      const [nextModel, cmd] = builder.update(currentModel, msg);
      if (nextModel !== currentModel) {
        newComponentModels.set(key, nextModel);
        anyComponentChanged = true;
      }
      if (cmd) {
        cmds.push(cmd);
      }
    }
    if (anyComponentChanged) {
      const next = new _GeneratedModel(
        this.#userState,
        Array.from(this.#componentBuilders.entries()).map(([key, builder]) => ({
          key,
          builder
        })),
        this.#keyHandlers,
        this.#messageHandlers,
        this.#initHandler,
        this.#viewFn,
        newComponentModels
      );
      return [next, cmds.length > 0 ? batch(...cmds) : null];
    }
    return [this, cmds.length > 0 ? batch(...cmds) : null];
  }
  view() {
    if (!this.#viewFn) {
      return "";
    }
    const componentViews = this.#buildComponentViews();
    const node = this.#viewFn({
      state: this.#userState,
      components: componentViews
    });
    return render(node);
  }
  #buildComponentViews() {
    const views = {};
    for (const [key, builder] of this.#componentBuilders) {
      const model = this.#componentModels.get(key);
      if (model !== void 0) {
        views[key] = componentView(builder.view(model));
      }
    }
    return views;
  }
};
function code(options) {
  return {
    init() {
      const model = CodeModel.new({
        filesystem: options.filesystem,
        path: options.path,
        active: options.active ?? false,
        syntaxTheme: options.theme ?? "dracula",
        width: options.width ?? 0,
        height: options.height ?? 0
      });
      return [model, model.init()];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function filepicker(options) {
  return {
    init() {
      const [model, cmd] = FilepickerModel.new({
        filesystem: options.filesystem,
        path: options.path,
        currentDir: options.currentDirectory,
        height: options.height,
        showHidden: options.showHidden,
        allowedTypes: options.allowedExtensions,
        styles: options.styles
      });
      return [model, cmd];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function filetree(options) {
  return {
    init() {
      const model = FiletreeModel.new({
        filesystem: options.filesystem,
        path: options.path,
        currentDir: options.currentDirectory,
        showHidden: options.showHidden,
        height: options.height,
        width: options.width,
        styles: options.styles,
        keyMap: options.keyMap
      });
      return [model, model.init()];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function help(options) {
  const { keyMap } = options;
  const helpOpts = {
    width: options.width,
    showAll: options.showAll,
    shortSeparator: options.shortSeparator,
    fullSeparator: options.fullSeparator,
    ellipsis: options.ellipsis,
    styles: options.styles
  };
  return {
    init() {
      const model = HelpModel.new(helpOpts);
      return [model, null];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view(keyMap);
    }
  };
}
function helpBubble(options) {
  const title = options.title ?? "Help";
  const titleColor = options.titleColor ?? {
    background: { dark: "#5f5f87", light: "#d7d7ff" },
    foreground: { dark: "#ffffff", light: "#000000" }
  };
  const active = options.active ?? false;
  return {
    init() {
      const model = HelpBubble.new(active, title, titleColor, options.entries);
      return [model, null];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function list(options) {
  return {
    init() {
      const model = ListModel.new({
        items: options.items,
        delegate: options.delegate,
        height: options.height,
        width: options.width,
        title: options.title,
        showTitle: options.showTitle,
        showFilter: options.showFilter,
        showPagination: options.showPagination,
        showHelp: options.showHelp,
        showStatusBar: options.showStatusBar,
        filteringEnabled: options.filteringEnabled,
        styles: options.styles,
        keyMap: options.keyMap
      });
      return [model, model.init()];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
var StaticMarkdownModel = class {
  constructor(renderedContent) {
    this.renderedContent = renderedContent;
  }
  /**
   * Tea init hook (no-op).
   */
  init() {
    return null;
  }
  /**
   * Tea update hook (no-op - static content).
   */
  update(_msg) {
    return [this, null];
  }
  /**
   * Render the markdown content.
   */
  view() {
    return this.renderedContent;
  }
};
function markdown(options) {
  return {
    init() {
      const renderedContent = renderMarkdown(options.content, {
        width: options.width ?? 80
      });
      const model = new StaticMarkdownModel(renderedContent);
      return [model, null];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function paginator(options) {
  return {
    init() {
      const paginatorType = options.type === "dots" ? PaginatorType.Dots : PaginatorType.Arabic;
      const model = PaginatorModel.new({
        type: paginatorType,
        page: options.page,
        perPage: options.perPage,
        activeDot: options.activeDot,
        inactiveDot: options.inactiveDot,
        arabicFormat: options.arabicFormat,
        keyMap: options.keyMap
      }).setTotalPages(options.totalItems);
      return [model, null];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function progress(options = {}) {
  return {
    init() {
      const hasGradient = options.gradient !== void 0;
      const model = hasGradient ? ProgressModel.withGradient(
        options.gradient.start,
        options.gradient.end,
        {
          width: options.width,
          full: options.full,
          empty: options.empty,
          emptyColor: options.emptyColor,
          showPercentage: options.showPercentage,
          percentFormat: options.percentFormat,
          scaleGradient: options.gradient.scaleGradientToProgress,
          springFrequency: options.spring?.frequency,
          springDamping: options.spring?.damping,
          percentageStyle: options.percentageStyle
        }
      ) : ProgressModel.new({
        width: options.width,
        full: options.full,
        empty: options.empty,
        fullColor: options.fullColor,
        emptyColor: options.emptyColor,
        showPercentage: options.showPercentage,
        percentFormat: options.percentFormat,
        springFrequency: options.spring?.frequency,
        springDamping: options.spring?.damping,
        percentageStyle: options.percentageStyle
      });
      return [model, null];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function spinner(options = {}) {
  const spinnerOpts = {
    spinner: options.spinner ?? line,
    style: options.style ?? new Style()
  };
  return {
    init() {
      const model = new SpinnerModel(spinnerOpts);
      return [model, model.tick()];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function statusBar(options) {
  const firstColors = {
    foreground: options.first.foreground,
    background: options.first.background
  };
  const secondColors = {
    foreground: options.second.foreground,
    background: options.second.background
  };
  const thirdColors = {
    foreground: options.third.foreground,
    background: options.third.background
  };
  const fourthColors = {
    foreground: options.fourth.foreground,
    background: options.fourth.background
  };
  return {
    init() {
      const model = StatusbarModel.new(
        firstColors,
        secondColors,
        thirdColors,
        fourthColors
      );
      return [model, null];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function stopwatch(options = {}) {
  const { interval, autoStart = false } = options;
  const stopwatchOpts = {
    interval
  };
  return {
    init() {
      const model = StopwatchModel.new(stopwatchOpts);
      const cmd = autoStart ? model.start() : null;
      return [model, cmd];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function table(options) {
  const tableOpts = {
    columns: options.columns,
    rows: options.rows,
    height: options.height,
    width: options.width,
    focused: options.focused,
    bordered: options.bordered,
    borderStyle: options.borderStyle,
    styles: options.styles,
    keyMap: options.keyMap
  };
  return {
    init() {
      const model = TableModel.new(tableOpts);
      return [model, model.init()];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function textArea(options = {}) {
  const textareaOpts = {
    value: options.value,
    placeholder: options.placeholder,
    width: options.width,
    maxHeight: options.maxHeight,
    maxWidth: options.maxWidth,
    prompt: options.prompt ?? "",
    showLineNumbers: options.showLineNumbers ?? false,
    cursorMode: options.cursorMode,
    validate: options.validate,
    promptStyle: options.promptStyle ?? new Style(),
    textStyle: options.textStyle ?? new Style(),
    placeholderStyle: options.placeholderStyle ?? new Style(),
    cursorStyle: options.cursorStyle ?? new Style(),
    lineNumberStyle: options.lineNumberStyle ?? new Style(),
    keyMap: options.keyMap
  };
  return {
    init() {
      const model = TextareaModel.new(textareaOpts);
      const [focused, cmd] = model.focus();
      return [focused, cmd];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function textInput(options = {}) {
  const inputOpts = {
    placeholder: options.placeholder ?? "",
    width: options.width,
    echoMode: options.echoMode ?? EchoMode.Normal,
    charLimit: options.charLimit ?? 0,
    prompt: options.prompt ?? "",
    promptStyle: options.promptStyle ?? new Style(),
    textStyle: options.textStyle ?? new Style(),
    placeholderStyle: options.placeholderStyle ?? new Style(),
    validate: options.validate
  };
  return {
    init() {
      const model = TextInputModel.new(inputOpts);
      const [focused, cmd] = model.focus();
      return [focused, cmd];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function timer(options) {
  const timerOpts = {
    timeout: options.timeout,
    interval: options.interval
  };
  const autoStart = options.autoStart ?? true;
  return {
    init() {
      const model = TimerModel.new(timerOpts);
      const startCmd = autoStart ? model.start() : null;
      return [model, startCmd];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}
function viewport(options = {}) {
  const viewportOpts = {
    width: options.width,
    height: options.height,
    mouseWheelEnabled: options.mouseWheelEnabled,
    style: options.style
  };
  return {
    init() {
      let model = ViewportModel.new(viewportOpts);
      if (options.content !== void 0) {
        model = model.setContent(options.content);
      }
      return [model, model.init()];
    },
    update(model, msg) {
      return model.update(msg);
    },
    view(model) {
      return model.view();
    }
  };
}

export { AppBuilder, choose, code, createApp, divider, filepicker, filetree, help, helpBubble, hstack, list, map, markdown, paginator, progress, render, spacer, spinner, statusBar, stopwatch, table, text, textArea, textInput, timer, viewport, vstack, when };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map